((root, factory) => {
  const rules = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = rules;
  }

  if (root) {
    root.PetMealsDeliveryDateRules = rules;
  }
})(typeof window !== 'undefined' ? window : globalThis, () => {
  const TIME_ZONE = 'Asia/Manila';
  const DAY_BEFORE_CUTOFF_HOUR = 14;
  const CUTOFF_HOUR = 17;
  const MONTHS = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };
  const partsFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const longDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function getManilaParts(timestampMs) {
    const parts = Object.fromEntries(
      partsFormatter
        .formatToParts(new Date(timestampMs))
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)])
    );

    return {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour,
      minute: parts.minute,
      second: parts.second,
    };
  }

  function toDateKey({ year, month, day }) {
    return `${year}-${pad(month)}-${pad(day)}`;
  }

  function parseDateKey(dateKey) {
    const match = String(dateKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return { year, month, day };
  }

  function addDays(dateKey, days) {
    const parts = parseDateKey(dateKey);
    if (!parts) return null;

    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return toDateKey({
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    });
  }

  function getManilaDateKey(timestampMs) {
    return toDateKey(getManilaParts(timestampMs));
  }

  function isPastCutoff(timestampMs) {
    return getManilaParts(timestampMs).hour >= CUTOFF_HOUR;
  }

  function isPastDayBeforeCutoff(timestampMs) {
    return getManilaParts(timestampMs).hour >= DAY_BEFORE_CUTOFF_HOUR;
  }

  function getDeliveryPeriodTiming(deliveryPeriod) {
    const normalizedPeriod = String(deliveryPeriod || '')
      .trim()
      .toUpperCase();

    if (normalizedPeriod.includes('DAY BEFORE') || normalizedPeriod.includes('DAY-BEFORE')) {
      return 'day_before';
    }

    if (normalizedPeriod.includes('SAME DAY') || normalizedPeriod.includes('SAME-DAY')) {
      return 'same_day';
    }

    return '';
  }

  function getMinimumDeliveryDateKey(timestampMs, deliveryPeriod = '') {
    const dayBeforeCutoffApplies =
      getDeliveryPeriodTiming(deliveryPeriod) === 'day_before' &&
      isPastDayBeforeCutoff(timestampMs);
    const minimumDaysAhead =
      isPastCutoff(timestampMs) || dayBeforeCutoffApplies ? 2 : 1;

    return addDays(getManilaDateKey(timestampMs), minimumDaysAhead);
  }

  function isWeekend(dateKey) {
    const parts = parseDateKey(dateKey);
    if (!parts) return false;

    const day = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
    return day === 0 || day === 6;
  }

  function getEarliestSelectableDateKey(timestampMs, deliveryPeriod = '') {
    let dateKey = getMinimumDeliveryDateKey(timestampMs, deliveryPeriod);

    while (dateKey && isWeekend(dateKey)) {
      dateKey = addDays(dateKey, 1);
    }

    return dateKey;
  }

  function isDeliveryDateAvailable(dateKey, timestampMs, deliveryPeriod = '') {
    return Boolean(
      parseDateKey(dateKey) &&
      dateKey >= getMinimumDeliveryDateKey(timestampMs, deliveryPeriod) &&
      !isWeekend(dateKey)
    );
  }

  function parseDisplayDate(dateLabel) {
    const match = String(dateLabel || '')
      .trim()
      .match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
    if (!match || !MONTHS[match[1]]) return null;

    const dateKey = toDateKey({
      year: Number(match[3]),
      month: MONTHS[match[1]],
      day: Number(match[2]),
    });

    return parseDateKey(dateKey) ? dateKey : null;
  }

  function formatDateKey(dateKey) {
    const parts = parseDateKey(dateKey);
    if (!parts) return '';

    return longDateFormatter.format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
  }

  function getItemDeliveryDate(item) {
    const properties = item?.properties || {};
    if (properties['Selected Consumption Dates']) {
      return properties['Selected Consumption Dates'];
    }

    return properties._shipfee === 'true' ? properties['Delivery Date'] || '' : '';
  }

  function getItemDeliveryPeriod(item) {
    const properties = item?.properties || {};
    if (properties['Delivery Period']) return properties['Delivery Period'];

    const periodOption = (item?.options_with_values || []).find((option) =>
      String(option?.name || '')
        .toLowerCase()
        .includes('delivery period')
    );
    if (periodOption?.value) return periodOption.value;

    const variantPeriod = (item?.variant_options || []).find((option) =>
      getDeliveryPeriodTiming(option)
    );
    if (variantPeriod) return variantPeriod;

    return getDeliveryPeriodTiming(item?.variant_title) ? item.variant_title : '';
  }

  function validateCart(cart, timestampMs) {
    const deliveryItems = (cart?.items || []).filter((item) => getItemDeliveryDate(item));
    const invalidItems = deliveryItems.filter((item) => {
      const dateKey = parseDisplayDate(getItemDeliveryDate(item));
      const deliveryPeriod = getItemDeliveryPeriod(item);
      return !dateKey || !isDeliveryDateAvailable(dateKey, timestampMs, deliveryPeriod);
    });
    const invalidPeriodItems = invalidItems.filter((item) => {
      const dateKey = parseDisplayDate(getItemDeliveryDate(item));
      const deliveryPeriod = getItemDeliveryPeriod(item);

      return Boolean(
        dateKey &&
        getDeliveryPeriodTiming(deliveryPeriod) === 'day_before' &&
        isDeliveryDateAvailable(dateKey, timestampMs) &&
        !isDeliveryDateAvailable(dateKey, timestampMs, deliveryPeriod)
      );
    });

    return {
      valid: invalidItems.length === 0,
      invalidItems,
      invalidPeriodItems,
      invalidMealItems: invalidItems.filter((item) => item?.properties?._shipfee !== 'true'),
      earliestDateKey: getEarliestSelectableDateKey(timestampMs),
      earliestDayBeforeDateKey: getEarliestSelectableDateKey(timestampMs, 'DAY BEFORE'),
    };
  }

  return Object.freeze({
    DAY_BEFORE_CUTOFF_HOUR,
    TIME_ZONE,
    CUTOFF_HOUR,
    addDays,
    formatDateKey,
    getDeliveryPeriodTiming,
    getEarliestSelectableDateKey,
    getManilaDateKey,
    getManilaParts,
    getMinimumDeliveryDateKey,
    isDeliveryDateAvailable,
    isPastDayBeforeCutoff,
    isPastCutoff,
    isWeekend,
    parseDateKey,
    parseDisplayDate,
    validateCart,
  });
});
